/**
 * Support Task Service
 * Handles task/ticket management API calls
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { SUPPORT_TASK_ENDPOINTS, buildQueryString } from '../endpoints';

export const SupportTaskStatus = {
  OPEN: 'Open',
  IN_PROGRESS: 'InProgress',
  ON_HOLD: 'OnHold',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
  BACKLOG: 'Backlog',
};

export const SupportTaskType = {
  BUG: 'Bug',
  FEATURE: 'Feature',
  IMPROVEMENT: 'Improvement',
  QUESTION: 'Question',
  PROBLEM: 'Problem',
};

export const SupportTaskPriority = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
};

export const TaskProjectScope = {
  NONE: 'None',
  PORTAL: 'Portal',
  MOBILE_APP: 'MobileApp',
};

// Internal numeric values for bitwise operations
const SCOPE_FLAGS = {
  None: 0,
  Portal: 1,
  MobileApp: 2,
};

/**
 * Convert a backend scope string (e.g. "Portal", "Portal, MobileApp", "None")
 * to a numeric bitmask for internal toggle logic.
 */
export const scopeStringToFlags = (scope) => {
  if (!scope || scope === 'None') return 0;
  const str = String(scope);
  let flags = 0;
  if (str.includes('Portal')) flags |= SCOPE_FLAGS.Portal;
  if (str.includes('MobileApp')) flags |= SCOPE_FLAGS.MobileApp;
  return flags;
};

/**
 * Convert a numeric bitmask back to the backend string format.
 * e.g. 0 → "None", 1 → "Portal", 2 → "MobileApp", 3 → "Portal, MobileApp"
 */
export const scopeFlagsToString = (flags) => {
  if (!flags) return 'None';
  const parts = [];
  if (flags & SCOPE_FLAGS.Portal) parts.push('Portal');
  if (flags & SCOPE_FLAGS.MobileApp) parts.push('MobileApp');
  return parts.length > 0 ? parts.join(', ') : 'None';
};

export const getProjectScopeLabel = (scope) => {
  const flags = typeof scope === 'number' ? scope : scopeStringToFlags(scope);
  if (!flags) return 'None';
  const parts = [];
  if (flags & SCOPE_FLAGS.Portal) parts.push('Portal');
  if (flags & SCOPE_FLAGS.MobileApp) parts.push('Mobile App');
  return parts.join(', ');
};

/**
 * Returns array of scope string keys that are active.
 * e.g. "Portal, MobileApp" → ["Portal", "MobileApp"]
 */
export const getProjectScopeItems = (scope) => {
  const flags = typeof scope === 'number' ? scope : scopeStringToFlags(scope);
  const items = [];
  if (flags & SCOPE_FLAGS.Portal) items.push('Portal');
  if (flags & SCOPE_FLAGS.MobileApp) items.push('MobileApp');
  return items;
};

export const getTaskStatusLabel = (status) => {
  const labels = {
    'Open': 'Open',
    'InProgress': 'In Progress',
    'OnHold': 'On Hold',
    'Resolved': 'Resolved',
    'Closed': 'Closed',
    'Backlog': 'Backlog',
  };
  return labels[status] ?? 'Unknown';
};

export const getTaskTypeLabel = (type) => {
  const labels = {
    'Bug': 'Bug',
    'Feature': 'Feature',
    'Improvement': 'Improvement',
    'Question': 'Question',
    'Problem': 'Problem',
  };
  return labels[type] ?? 'Unknown';
};

export const getTaskPriorityLabel = (priority) => {
  const labels = {
    'Low': 'Low',
    'Medium': 'Medium',
    'High': 'High',
    'Critical': 'Critical',
  };
  return labels[priority] ?? 'Unknown';
};

export const TaskRelationType = {
  RELATED: 'Related',
  BLOCKS: 'Blocks',
  BLOCKED_BY: 'BlockedBy',
  DUPLICATE_OF: 'DuplicateOf',
  DUPLICATED_BY: 'DuplicatedBy',
};

export const getRelationTypeLabel = (type) => {
  const labels = {
    'Related': 'Related',
    'Blocks': 'Blocks',
    'BlockedBy': 'Blocked By',
    'DuplicateOf': 'Duplicate Of',
    'DuplicatedBy': 'Duplicated By',
  };
  return labels[type] ?? 'Related';
};

export const RELATION_TYPE_OPTIONS = [
  { value: 0, label: 'Related' },
  { value: 1, label: 'Blocks' },
  { value: 2, label: 'Blocked By' },
  { value: 3, label: 'Duplicate Of' },
  { value: 4, label: 'Duplicated By' },
];

const supportTaskService = {
  getTasks: async (params = {}) => {
    const queryString = buildQueryString(params);
    return apiRequest(() =>
      axiosInstance.get(`${SUPPORT_TASK_ENDPOINTS.LIST}${queryString}`)
    );
  },

  getTaskById: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(SUPPORT_TASK_ENDPOINTS.GET_BY_ID(id))
    );
  },

  createTask: async (data) => {
    return apiRequest(() =>
      axiosInstance.post(SUPPORT_TASK_ENDPOINTS.CREATE, data)
    );
  },

  updateTask: async (id, data) => {
    return apiRequest(() =>
      axiosInstance.put(SUPPORT_TASK_ENDPOINTS.UPDATE(id), data)
    );
  },

  updateTaskStatus: async (id, data) => {
    return apiRequest(() =>
      axiosInstance.put(SUPPORT_TASK_ENDPOINTS.UPDATE_STATUS(id), data)
    );
  },

  deleteTask: async (id) => {
    return apiRequest(() =>
      axiosInstance.delete(SUPPORT_TASK_ENDPOINTS.DELETE(id))
    );
  },

  reorderTask: async (data) => {
    return apiRequest(() =>
      axiosInstance.put(SUPPORT_TASK_ENDPOINTS.REORDER, data)
    );
  },

  getComments: async (taskId) => {
    return apiRequest(() =>
      axiosInstance.get(SUPPORT_TASK_ENDPOINTS.COMMENTS(taskId))
    );
  },

  addComment: async (taskId, data) => {
    return apiRequest(() =>
      axiosInstance.post(SUPPORT_TASK_ENDPOINTS.ADD_COMMENT(taskId), data)
    );
  },

  deleteComment: async (commentId) => {
    return apiRequest(() =>
      axiosInstance.delete(SUPPORT_TASK_ENDPOINTS.DELETE_COMMENT(commentId))
    );
  },

  addAttachment: async (taskId, data) => {
    return apiRequest(() =>
      axiosInstance.post(SUPPORT_TASK_ENDPOINTS.ADD_ATTACHMENT(taskId), data)
    );
  },

  deleteAttachment: async (attachmentId) => {
    return apiRequest(() =>
      axiosInstance.delete(SUPPORT_TASK_ENDPOINTS.DELETE_ATTACHMENT(attachmentId))
    );
  },

  analyzeSentryIssue: async (issueId) => {
    return apiRequest(() =>
      axiosInstance.post(SUPPORT_TASK_ENDPOINTS.ANALYZE_SENTRY(issueId))
    );
  },

  getAssignableUsers: async () => {
    return apiRequest(() =>
      axiosInstance.get(SUPPORT_TASK_ENDPOINTS.ASSIGNABLE_USERS)
    );
  },

  // Relations
  getRelatedTasks: async (taskId) => {
    return apiRequest(() =>
      axiosInstance.get(SUPPORT_TASK_ENDPOINTS.RELATIONS(taskId))
    );
  },

  addRelation: async (taskId, data) => {
    return apiRequest(() =>
      axiosInstance.post(SUPPORT_TASK_ENDPOINTS.RELATIONS(taskId), data)
    );
  },

  removeRelation: async (relationId) => {
    return apiRequest(() =>
      axiosInstance.delete(SUPPORT_TASK_ENDPOINTS.REMOVE_RELATION(relationId))
    );
  },

  searchTasks: async (search, excludeTaskId) => {
    const params = { search };
    if (excludeTaskId) params.excludeTaskId = excludeTaskId;
    const queryString = buildQueryString(params);
    return apiRequest(() =>
      axiosInstance.get(`${SUPPORT_TASK_ENDPOINTS.SEARCH}${queryString}`)
    );
  },
};

export default supportTaskService;
